import type { RecordMerchant } from 'features/table-columns/cells'
import { constructorDocument, DocumentParams } from 'models/Document'
import moment from 'moment'

import { formatMoney } from 'utils'

export interface AccountsPayableCategory {
  color: string
  name: string
  uuid: string
}

export interface AccountsPayableCostCenter {
  color: string
  name: string
  uuid: string
}

export interface AccountsPayableAllocation {
  uuid: string
  category: AccountsPayableCategory
  costCenter: AccountsPayableCostCenter
  amount: number
  percentage: number
}

export type ThirdPartyType =
  | 'employee'
  | 'partner'
  | 'supplier'
  | 'investor'
  | 'tenant'
  | 'service_provider'
  | 'other'

/** @deprecated Use ThirdParty instead */
export type PayeeType = ThirdPartyType

export interface ThirdParty {
  uuid: string
  name: string
  email: string
  phone: string
  notes?: string
  thirdPartyType?: ThirdPartyType
}

/** @deprecated Use ThirdParty instead */
export type AccountsPayablePayee = ThirdParty

export type SourceType = 'credit_card' | 'bank_account' | 'cash' | 'other'

export interface AccountsPayableSource {
  uuid: string
  name: string
  sourceType: SourceType
}

export interface AccountsPayableItem {
  uuid: string
  description: string
  amountUnit: number
  quantity: number
  amountBilled: number
}

export const frequencyTypeMapper = {
  days: 1,
  weeks: 2,
  months: 3,
  years: 4
}

// A API serializa `cost_type` como enum do Rails, ou seja, a STRING
// ('fixed' | 'variable' | 'single' | 'installment') — nunca o inteiro.
// Este mapper existe pro caminho inverso: os forms enviam o inteiro.
export const costTypeMapper = {
  fixed: 1,
  variable: 2,
  single: 3
}

export type CostType = 'fixed' | 'variable' | 'single' | 'installment'

export type ContaAzulInfoStatus =
  | 'pending'
  | 'enqueued'
  | 'syncing'
  | 'synced'
  | 'error'

export interface ContaAzulInfo {
  uuid: string
  status: ContaAzulInfoStatus
  caExternalId: string | null
  caProtocolId: string | null
  caInstallmentId: string | null
  caBaixaId: string | null
  lastError: string | null
  syncedAt: string | null
}

export interface AccountsPayable {
  uuid: string
  // SC-10999: só nas respostas agregadas (visão multi-empresas)
  merchant?: RecordMerchant | null
  payee: ThirdParty
  allocations: AccountsPayableAllocation[]
  filteredAmountBilled?: number
  filteredAmountPaid?: number
  competenceDate: string
  dueDate: string
  firstDueDate?: string
  costType: CostType
  items: AccountsPayableItem[]
  amountBilled: number
  description?: string
  totalCycles: number
  recurrenceCycle: number
  recurrenceUuid: string
  frequencyNumber?: number
  frequencyType?: string
  source?: AccountsPayableSource
  datePaid?: string
  amountPaid?: number
  status?: string
  documentFile?: DocumentParams
  documentFiles: DocumentParams[]
  contaAzulInfo?: ContaAzulInfo | null
  tags?: Array<{
    id: string
    name: string
    color?: string
  }>
}

export interface RecurrenceAccountsPayable {
  uuid: string
  dueDate: string
  datePaid: string
  amountBilled: string
  amountPaid: string
  status: string
  recurrenceCycle?: number | null
  contaAzulInfo?: ContaAzulInfo | null
}

export const constructorRecurrence = (data: any) => {
  return {
    uuid: data.uuid,
    dueDate: moment(data.due_date).format('DD/MM/YYYY') || '',
    datePaid: data.date_paid
      ? moment(data.date_paid).format('DD/MM/YYYY')
      : '-',
    amountBilled: formatMoney(data.amount_billed) || null,
    amountPaid: formatMoney(data.amount_paid) || null,
    status: data.status,
    recurrenceCycle: data.recurrence_cycle,
    contaAzulInfo: data?.conta_azul_info
      ? {
          uuid: data.conta_azul_info.uuid,
          status: data.conta_azul_info.status,
          caExternalId: data.conta_azul_info.ca_external || null,
          caProtocolId: data.conta_azul_info.ca_protocol || null,
          caInstallmentId: data.conta_azul_info.ca_installment || null,
          caBaixaId: data.conta_azul_info.ca_baixa || null,
          lastError: data.conta_azul_info.last_error || null,
          syncedAt: data.conta_azul_info.synced_at || null
        }
      : null
  } as RecurrenceAccountsPayable
}

export const constructorAccountsPayable = (data: any) => {
  return {
    uuid: data?.uuid,
    payee: {
      uuid: data?.third_party?.uuid || data?.accounts_payable_payee?.uuid || '',
      name: data?.third_party?.name || data?.accounts_payable_payee?.name || '',
      email:
        data?.third_party?.email || data?.accounts_payable_payee?.email || '',
      phone:
        data?.third_party?.phone || data?.accounts_payable_payee?.phone || '',
      notes:
        data?.third_party?.notes || data?.accounts_payable_payee?.notes || '',
      thirdPartyType:
        data?.third_party?.third_party_type ||
        data?.accounts_payable_payee?.payee_type ||
        ''
    },
    allocations: (data?.accounts_payable_allocations || []).map(
      (allocation: any) => ({
        uuid: allocation?.uuid || '',
        amount: Number(allocation?.amount) || 0,
        percentage: Number(allocation?.percentage) || 0,
        category: {
          uuid: allocation?.accounts_payable_category?.uuid || '',
          name: allocation?.accounts_payable_category?.name || '',
          color: allocation?.accounts_payable_category?.color || ''
        },
        costCenter: {
          uuid: allocation?.accounts_payable_cost_center?.uuid || '',
          name: allocation?.accounts_payable_cost_center?.name || '',
          color: allocation?.accounts_payable_cost_center?.color || ''
        }
      })
    ),
    firstDueDate: data?.first_due_date,
    // Recorrência serializa first_due_date/first_competence_date (não due_date/competence_date).
    // Sem o fallback, o form abria com data undefined e o submit enviava HOJE, resetando o
    // vencimento inicial da recorrência e destruindo o histórico no recálculo do backend.
    competenceDate: data?.competence_date ?? data?.first_competence_date,
    dueDate: data?.due_date ?? data?.first_due_date,
    costType: data?.cost_type,
    items: data?.accounts_payable_items.map((item: any) => ({
      uuid: item?.uuid,
      description: item?.description,
      amountUnit: Number(item?.amount_unit),
      quantity: Number(item?.quantity),
      amountBilled: Number(item?.amount_billed)
    })),
    amountBilled: data?.amount_billed,
    description: data?.description || '',
    totalCycles: data?.total_cycles,
    recurrenceCycle: data?.recurrence_cycle,
    recurrenceUuid: data?.accounts_payable_recurrence_uuid,
    merchant: data?.merchant ?? null,
    frequencyNumber: data?.frequency_number,
    frequencyType: data?.frequency_type,
    source: data?.accounts_payable_source
      ? {
          uuid: data?.accounts_payable_source.uuid,
          name: data?.accounts_payable_source.name,
          sourceType: data?.accounts_payable_source.source_type
        }
      : null,
    datePaid: data?.date_paid,
    amountPaid: Number(data?.amount_paid),
    ...(data?.filtered_amount_billed != null
      ? { filteredAmountBilled: Number(data?.filtered_amount_billed) }
      : {}),
    ...(data?.filtered_amount_paid != null
      ? { filteredAmountPaid: Number(data?.filtered_amount_paid) }
      : {}),
    status: data?.status,
    ...(data?.document_file
      ? { documentFile: constructorDocument(data?.document_file) }
      : {}),
    // SC-10805: a despesa passou a aceitar até 3 comprovantes. `documentFile` continua
    // apontando para o primeiro, para as telas que ainda mostram um só.
    documentFiles: (data?.document_files ?? []).map((document: any) =>
      constructorDocument(document)
    ),
    tags:
      data?.tags?.map((tag: any) => ({
        id: tag.uuid || tag.id,
        name: tag.name,
        color: tag.color
      })) || [],
    contaAzulInfo: data?.conta_azul_info
      ? {
          uuid: data.conta_azul_info.uuid,
          status: data.conta_azul_info.status,
          caExternalId: data.conta_azul_info.ca_external || null,
          caProtocolId: data.conta_azul_info.ca_protocol || null,
          caInstallmentId: data.conta_azul_info.ca_installment || null,
          caBaixaId: data.conta_azul_info.ca_baixa || null,
          lastError: data.conta_azul_info.last_error || null,
          syncedAt: data.conta_azul_info.synced_at || null
        }
      : null
  } as AccountsPayable
}

export type CalendarEventsData = {
  amount: string
  date: string
  has_unpaid_accounts_payable: boolean
}
export type StatisticsExpenses = {
  total: string
  total_count: number
  total_paid: string
  total_paid_count: number
  total_to_pay: string
}
export type MetaExpenses = {
  current_page: number
  next_page: number
  prev_page: number
  total_count: number
  total_pages: number
}

export type DashboardInsights = {
  accounts_payables_billed: BilledInsight[]
  accounts_payables_paid: PaidInsight[]
}

export type BilledInsight = {
  amount_billed_fixed: string
  amount_billed_single: string
  amount_billed_variable: string
  amount_billed_installment: string
  reference_date: string
}

export type PaidInsight = {
  amount_paid_fixed: string
  amount_paid_single: string
  amount_paid_variable: string
  amount_paid_installment: string
  reference_date: string
}
export type PieChartInsights = {
  category: CategoryInsights
  costCenter: CostCenter
}
export type CategoryInsights = {
  categoryBilled: Record<string, PieChartItem>
  categoryPaid: Record<string, PieChartItem>
}
export type CostCenter = {
  costCenterBilled: Record<string, PieChartItem>
  costCenterPaid: Record<string, PieChartItem>
}
export type PieChartItem = {
  color: string
  total: string
  // Fatia que agrupa o valor sem a dimensão do gráfico (despesa sem categoria
  // ou sem centro de custo). O backend marca com esta flag em vez de depender do
  // rótulo, para não confundir com uma categoria/centro de mesmo nome.
  residual?: boolean
}
